import type { ChangeEventHandler, MouseEventHandler } from "react"
import { useCallback } from "react"

import {
  ADDER_CATEGORY,
  ADDER_PROPOSAL_USE_TYPE,
  MODULE_POWUR_OS_EQUIPMENT_ID,
} from "@logic/lib/powur-os"
import { atom, selector, useRecoilValue } from "recoil"

import type { ProposalAdderObject } from "~/graphql/client"

import {
  getDesignCostItems,
  PricingSummaryLineItemType,
  type Design,
} from "./designs/design-model"
import { getSolarModuleEquipmentFromDesign } from "./designs/solar-module"
import { useProposalContext } from "./proposal/proposal-context"
import { proposalLocalState } from "./proposal/proposal-state"

const CATEGORY_IDS = {
  ALL_ADDERS: 0,
  EV_ADDERS: -3,
  REBATES: -1,
  WARRANTIES: -2,
} as const

type ToggleAdderSelected = (id: string, design: Design) => MouseEventHandler
type SetAdderValue = (id: string) => ChangeEventHandler<HTMLInputElement>

const allProposalAddersState = selector<ProposalAdderObject[]>({
  key: `allProposalAdders`,
  get: ({ get }) => {
    const proposal = get(proposalLocalState)

    const adders =
      proposal.proposalAdders
        .filter((pa) => {
          const isMultipleArrayAdder = pa.adder.title
            .toLowerCase()
            .startsWith(`multiple arrays`)

          const isVariableAdder = pa.cost === 0 && pa.isVariable
          const costGreaterThanZero = pa.cost > 0

          return (
            !isMultipleArrayAdder && (isVariableAdder || costGreaterThanZero)
          )
        })
        .sort(({ adder: { title } }, { adder: { title: secondTitle } }) =>
          title > secondTitle ? -1 : 1,
        ) ?? []

    return adders
  },
})

export const selectedCategoryState = atom<ADDER_CATEGORY>({
  key: `selectedCategory`,
  default: ADDER_CATEGORY[`All Adders`],
})

const allNonAutoAdders = selector<ProposalAdderObject[]>({
  key: `allNonAutoAdders`,
  get: ({ get }) => {
    const proposalAdders = get(allProposalAddersState)

    return proposalAdders.filter(
      (i) => i.adder.proposalUseTypeId === ADDER_PROPOSAL_USE_TYPE.MANUAL,
    )
  },
})

const selectedAddersState = selector<ProposalAdderObject[]>({
  key: `selectedAdders`,
  get: ({ get }) => {
    const proposalAdders: ProposalAdderObject[] = get(allProposalAddersState)
    const selectedAdders = proposalAdders.filter(
      (adder) => adder.selected === true,
    )

    return selectedAdders
  },
})

const getAddersForSelectedCategory = selector<ProposalAdderObject[]>({
  key: `addersForCategory`,
  get: ({ get }) => {
    const category = get(selectedCategoryState)
    const proposalAdders = get(allNonAutoAdders)

    const addersForCategory =
      category === ADDER_CATEGORY[`All Adders`]
        ? proposalAdders
        : proposalAdders.filter(({ adder }) => adder.category.id === category)

    return addersForCategory
  },
})

export const categoriesWithAddersState = selector<number[]>({
  key: `categoriesWithAddersState`,
  get: ({ get }) => {
    const adders = get(allNonAutoAdders)

    const categories = [
      CATEGORY_IDS.ALL_ADDERS,
      ...new Set(
        adders
          .map(({ adder }) => adder.powurOsCategoryId)
          .filter((c) => typeof c === `number`),
      ),
      CATEGORY_IDS.EV_ADDERS,
      CATEGORY_IDS.REBATES,
      CATEGORY_IDS.WARRANTIES,
    ] as number[]

    return categories
  },
})

export const useAdders = (): {
  proposalAdders: ProposalAdderObject[]
  addersForCategory: ProposalAdderObject[]
  selectedAdders: ProposalAdderObject[]
  setAdderPrice: SetAdderValue
  toggleAdderSelected: ToggleAdderSelected
  setAdderCustomQuantity: SetAdderValue
} => {
  const { changeLocal, local } = useProposalContext()
  const proposalAdders = useRecoilValue(allProposalAddersState)
  const selectedAdders = useRecoilValue(selectedAddersState)
  const addersForCategory = useRecoilValue(getAddersForSelectedCategory)

  const updateProposalAdders = useCallback(
    (updatedAdders: ProposalAdderObject[]) => {
      const adderMap = new Map<string, ProposalAdderObject>()
      local.proposalAdders.forEach((pa) => {
        adderMap.set(pa.adder.id, pa)
      })
      updatedAdders.forEach((pa) => {
        adderMap.set(pa.adder.id, pa)
      })

      changeLocal({
        proposalAdders: Array.from(adderMap.values()),
      })
    },
    [local.proposalAdders, changeLocal],
  )

  const updateAdders = useCallback(
    (updatedAdders: ProposalAdderObject[]) => {
      const newAdders = proposalAdders.map((proposalAdder) => {
        const replacement = updatedAdders.find(
          (pa) => pa.adder.id === proposalAdder.adder.id,
        )
        if (replacement) {
          return replacement
        }

        return proposalAdder
      })
      updateProposalAdders(newAdders)
    },
    [proposalAdders, updateProposalAdders],
  )

  const setAdderPrice: SetAdderValue = (adderId) => (e) => {
    const proposalAdder = proposalAdders.find((pa) => pa.adder.id === adderId)
    if (proposalAdder) {
      const updatedAdder: ProposalAdderObject = {
        ...proposalAdder,
        customPrice: parseFloat(e.target.value),
        selected: e.target.value !== `0` && e.target.value !== ``,
      }
      updateAdders([updatedAdder])
    }
  }

  const toggleAdderSelected: ToggleAdderSelected = (
    adderId: string,
    design: Design,
  ) => {
    return (e) => {
      e.stopPropagation()
      const proposalAdder = proposalAdders.find((pa) => pa.adder.id === adderId)
      if (proposalAdder) {
        const selected = !proposalAdder?.selected
        const updatedAdder: ProposalAdderObject = {
          ...proposalAdder,
          selected,
        }
        if (!selected) {
          if (proposalAdder.customPrice) {
            updatedAdder.customPrice = 0
          }
          if (proposalAdder.customQuantity) {
            updatedAdder.customQuantity = 0
          }
        }

        const proposalCostLineItems = local.proposalCost.lineItems.map(
          (item) => ({
            ...item,
            proposalCostLineItemId: null,
            proposalCostId: null,
          }),
        )
        // calculate cost of adder to update local total cost
        const totalAdderPrice = calculateAdderCost(
          updatedAdder,
          proposalCostLineItems,
          getAdderQuantityDesign(design),
        )

        // if category doesn't allow multiples we need to deselect all other adders
        if (
          !proposalAdder.adder.category.allowMultiples &&
          updatedAdder.selected
        ) {
          const others = proposalAdders
            .filter(
              (pa) =>
                pa.adder.id !== adderId &&
                pa.adder.category.id === proposalAdder.adder.category.id,
            )
            .map((a) => ({ ...a, selected: false }))
          // calculate cost of other adders to update local total cost
          const otherAddersPrice = others.reduce(
            (acc, a) =>
              acc +
              calculateAdderCost(
                a,
                proposalCostLineItems,
                getAdderQuantityDesign(design),
              ),
            0,
          )
          changeLocal({
            proposalCost: {
              ...local.proposalCost,
              totalCost:
                local.proposalCost.totalCost +
                (totalAdderPrice - otherAddersPrice),
            },
          })
          updateAdders([updatedAdder, ...others])
        } else {
          // update local total cost
          changeLocal({
            proposalCost: {
              ...local.proposalCost,
              totalCost:
                local.proposalCost.totalCost +
                (selected ? totalAdderPrice : -totalAdderPrice),
            },
          })
          updateAdders([updatedAdder])
        }
      }
    }
  }

  const setAdderCustomQuantity: SetAdderValue = (adderId: string) => {
    return (e) => {
      e.stopPropagation()

      const adder = proposalAdders.find((pa) => pa.adder.id === adderId)

      if (adder) {
        const updatedAdder: ProposalAdderObject = {
          ...adder,
          customQuantity: parseInt(e.target.value),
          selected: e.target.value !== `0` && e.target.value !== ``,
        }
        updateAdders([updatedAdder])
      }
    }
  }

  return {
    proposalAdders,
    addersForCategory,
    selectedAdders,
    setAdderPrice,
    toggleAdderSelected,
    setAdderCustomQuantity,
  }
}

type ProposalAdder = {
  createdAt: Date
  updatedAt: Date
  customPrice: number | null
  customQuantity: number | null
  selected: boolean
  powurOsAdderId: number
  proposalId: string
  cost: number | null
  end: Date | null
  isRequired: boolean | null
  isVariable: boolean | null
  start: Date | null
}

type ProposalCostLineItem = {
  id: string
  createdAt: Date
  updatedAt: Date
  displayName: string
  type: string
  cost: number
  quantity: number
  taxable: boolean
  proposalCostId: string | null
  proposalCostLineItemId: string | null
  subType: string | null
  powurOsId: number | null
}

enum UNIT_OF_MEASURE {
  WATT = 0,
  UNIT = 1,
  FOOT = 2,
  SQFT = 3,
  PANEL = 4,
  PROJECT = 5,
  ARRAY = 6,
  SQ = 7,
}

type AdderQuantityDesign = {
  installAreasToPanels: Array<[string, string[]]>
  panelCountOverride?: number
  panels: Record<string, { isActive: boolean | null }>
  solarModule: { wattage: number | null } | null
  source?: `aurora` | `vision` | null
  systemSizeKw?: number | null
}

const getAdderQuantityDesign = (design: Design): AdderQuantityDesign => ({
  installAreasToPanels: design.installAreasToPanels,
  panelCountOverride:
    design.source === `aurora`
      ? design.designEquipment.find(
          ({ powurOsEquipmentTypeId }) =>
            powurOsEquipmentTypeId === MODULE_POWUR_OS_EQUIPMENT_ID,
        )?.quantity
      : undefined,
  panels: design.panels,
  solarModule: getSolarModuleEquipmentFromDesign(design),
  source: design.source,
  systemSizeKw: design.systemSizeKw,
})

export function calculateAdderCost(
  proposalAdder: ProposalAdder & {
    adder: {
      isVariable: boolean | null
      title: string
      unit: UNIT_OF_MEASURE | null
    }
  },
  proposalLineItems: ProposalCostLineItem[],
  design: AdderQuantityDesign,
  isOsAdder = false,
): number {
  const { adder } = proposalAdder
  if (
    adder?.isVariable ||
    adder?.title.toLowerCase().includes(`multiple arrays`)
  ) {
    const customCost = proposalAdder.customPrice ?? 0
    const qty = proposalAdder.customQuantity ?? 0
    const finalCost = isOsAdder ? customCost : customCost * qty

    return finalCost
  }

  const lineItemAdders = proposalLineItems.filter(
    (lineItem) => lineItem.type === PricingSummaryLineItemType.ADDER,
  )

  const proposalAdderLineItem = lineItemAdders.find(
    (lineItem) => lineItem.displayName === proposalAdder.adder.title,
  )

  if (proposalAdderLineItem) {
    return proposalAdderLineItem.cost * proposalAdderLineItem.quantity
  }

  const cost = proposalAdder.cost ?? 0
  const qty = getQuantityForAdderUnit(proposalAdder, design)
  const finalCost = isOsAdder ? cost : cost * qty

  return finalCost
}

export function getQuantityForAdderUnit(
  proposalAdder: {
    adder: { unit: UNIT_OF_MEASURE | null; title: string }
    customQuantity?: number | null
  },
  design: AdderQuantityDesign,
): number {
  const { systemSizeInWatts, panels, arrays } = getDesignCostItems(design)

  switch (proposalAdder.adder?.unit) {
    case UNIT_OF_MEASURE.WATT:
      return design.source === `aurora` &&
        typeof design.systemSizeKw === `number` &&
        Number.isFinite(design.systemSizeKw) &&
        design.systemSizeKw > 0
        ? design.systemSizeKw * 1000
        : systemSizeInWatts

    case UNIT_OF_MEASURE.PANEL:
      return design.panelCountOverride ?? panels.length

    case UNIT_OF_MEASURE.ARRAY:
      if (
        proposalAdder.adder?.title.toLowerCase().startsWith(`multiple arrays`)
      ) {
        return arrays.length - 3
      }

      return arrays.length

    case UNIT_OF_MEASURE.FOOT:
      return proposalAdder.customQuantity ?? 0

    default:
      return 1
  }
}
